import { useMemo, useState } from 'react';
import type { DataISO, Dipendente, Evento, GiornoCalendario } from '../api/tipi.ts';
import type { Verificatore } from '../api/verifica.ts';
import type { NuovaRichiesta } from '../api/client.ts';
import { PannelloRichiesta } from '../richiesta/PannelloRichiesta.tsx';
import { MiniCalendario } from './MiniCalendario.tsx';
import {
  contaInAttesa,
  durataInGiorni,
  formattaIntervallo,
  prossimeAssenze,
  richiesteRifiutate,
} from './calendario.ts';
import stili from './Home.module.css';

/**
 * Schermata iniziale dopo l'accesso.
 *
 * Sostituisce il calendario come pagina d'ingresso. Far atterrare un utente base
 * su una griglia con 184 persone significa chiedergli di cercare la propria
 * riga in mezzo a tutte le altre: e' il difetto del file Excel di oggi, non
 * una cosa da riprodurre.
 *
 * L'ordine degli elementi segue quello delle domande che una persona si fa
 * aprendo l'applicazione: mi hanno approvato le ferie, quando sono le
 * prossime, come ne chiedo altre.
 */

export interface ProprietaHome {
  dipendente: Dipendente;
  eventi: readonly Evento[];
  giorni: readonly GiornoCalendario[];
  /** Se offrire il tipo "Riposo produzione": lo registra solo chi ha delega. */
  puoRiposoProduzione: boolean;
  /** Ultimo giorno prenotabile: il tetto dei campi data del pannello. */
  ultimoGiornoInseribile: DataISO;
  /** Primo giorno scrivibile: oggi, o il primo gennaio per chi corregge il passato. */
  primoGiornoInseribile: DataISO;
  /** L'anno di cui sono i dati: da luglio puo' essere il prossimo. */
  annoMostrato: number;
  verifica: Verificatore;
  onInvia: (richiesta: NuovaRichiesta) => Promise<void>;
  onAnnulla: (evento: Evento) => Promise<void>;
}

export function Home({
  dipendente,
  eventi,
  giorni,
  puoRiposoProduzione,
  ultimoGiornoInseribile,
  primoGiornoInseribile,
  annoMostrato,
  verifica,
  onInvia,
  onAnnulla,
}: ProprietaHome) {
  const oggi = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });
  // Quando il pannello si apre da un clic sul calendario porta con se' il
  // giorno scelto: chi tocca il 18 sta gia' dicendo quando vuole assentarsi.
  const [richiesta, setRichiesta] = useState<{ giorno?: DataISO } | null>(null);

  const prossime = useMemo(() => prossimeAssenze(eventi, oggi), [eventi, oggi]);
  const rifiutate = useMemo(() => richiesteRifiutate(eventi, oggi), [eventi, oggi]);
  const inAttesa = useMemo(() => contaInAttesa(eventi, oggi), [eventi, oggi]);

  return (
    <div className={stili.pagina}>
      <header className={stili.saluto}>
        <h1 className={stili.nome}>{dipendente.nomeCompleto}</h1>
        <p className={stili.reparto}>
          {dipendente.sedeNome} · {dipendente.repartoNome}
        </p>
      </header>

      {/* Il rifiuto sta PRIMA dell'attesa: e' una notizia su cui bisogna fare
          qualcosa, l'attesa e' solo un promemoria che il sistema sta
          lavorando. Se ci sono entrambe, quella che chiede attenzione va in
          cima. */}
      {rifiutate.length > 0 && (
        <p className={stili.avvisoRifiuto}>
          <strong>
            {rifiutate.length === 1
              ? 'Una tua richiesta è stata rifiutata.'
              : `${rifiutate.length} tue richieste sono state rifiutate.`}
          </strong>{' '}
          Il motivo è scritto qui sotto, in fondo alla pagina.
        </p>
      )}

      {inAttesa > 0 && (
        <p className={stili.avviso}>
          {inAttesa === 1
            ? 'Hai 1 richiesta in attesa di approvazione.'
            : `Hai ${inAttesa} richieste in attesa di approvazione.`}
        </p>
      )}

      {/* Quando si guarda un anno diverso da quello in corso il calendarietto
          si apre su gennaio: aprirlo sul mese di oggi mostrerebbe un mese che
          non appartiene all'anno caricato, quindi vuoto. */}
      <MiniCalendario
        eventi={eventi}
        giorni={giorni}
        {...(annoMostrato !== Number(oggi.slice(0, 4))
          ? { meseIniziale: { anno: annoMostrato, mese: 1 } }
          : {})}
        onGiorno={(giorno) => {
          // Un giorno gia' occupato non apre una richiesta nuova: aprirebbe
          // una sovrapposizione che il database rifiuterebbe comunque.
          if (giorno.evento) return;
          setRichiesta({ giorno: giorno.data });
        }}
      />

      <button type="button" className={stili.azione} onClick={() => setRichiesta({})}>
        + Richiedi assenza
      </button>

      <section className={stili.sezione}>
        <h2 className={stili.titoloSezione}>Prossime assenze</h2>
        {prossime.length === 0 ? (
          <p className={stili.vuoto}>Nessuna assenza in programma.</p>
        ) : (
          <ul className={stili.elenco}>
            {prossime.map((evento) => (
              <li key={evento.id} className={stili.voce}>
                <div className={stili.voceTesto}>
                  <span className={stili.voceTitolo}>{evento.etichetta}</span>
                  <span className={stili.voceData}>
                    {formattaIntervallo(evento.dataInizio, evento.dataFine)}
                    {evento.ore !== null && ` · ${formattaOre(evento.ore)}`}
                    {evento.ore === null &&
                      durataInGiorni(evento) > 1 &&
                      ` · ${durataInGiorni(evento)} giorni`}
                  </span>
                  {evento.nota && <span className={stili.voceNota}>{evento.nota}</span>}
                </div>
                <div className={stili.azioni}>
                  <Stato stato={evento.stato} />
                  <button
                    type="button"
                    className={stili.annulla}
                    onClick={() => void onAnnulla(evento)}
                    aria-label={`Annulla ${evento.etichetta} del ${evento.dataInizio}`}
                  >
                    Annulla
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {rifiutate.length > 0 && (
        <section className={stili.sezione}>
          <h2 className={stili.titoloSezione}>Richieste rifiutate</h2>
          <ul className={stili.elenco}>
            {rifiutate.map((evento) => (
              <li key={evento.id} className={stili.voce}>
                <div className={stili.voceTesto}>
                  <span className={stili.voceTitolo}>{evento.etichetta}</span>
                  <span className={stili.voceData}>
                    {formattaIntervallo(evento.dataInizio, evento.dataFine)}
                  </span>
                  {/* Il motivo e' la ragione per cui questa sezione esiste:
                      sapere di essere stati rifiutati senza sapere perche'
                      costringe a chiedere di persona. */}
                  {evento.motivoStato && (
                    <span className={stili.voceNota}>{evento.motivoStato}</span>
                  )}
                </div>
                <div className={stili.azioni}>
                  <Stato stato={evento.stato} />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {richiesta && (
        <PannelloRichiesta
          dipendenteId={dipendente.id}
          nomeDipendente={dipendente.nomeCompleto}
          {...(richiesta.giorno ? { giornoIniziale: richiesta.giorno } : {})}
          puoRiposoProduzione={puoRiposoProduzione}
          ultimoGiornoInseribile={ultimoGiornoInseribile}
          primoGiornoInseribile={primoGiornoInseribile}
          orarioSettimanale={dipendente.orarioSettimanale}
          verifica={verifica}
          onChiudi={() => setRichiesta(null)}
          onInvia={async (dati) => {
            await onInvia({ dipendenteId: dipendente.id, ...dati });
            setRichiesta(null);
          }}
        />
      )}
    </div>
  );
}

/** "1 ora", "0,5 ore", "4 ore": il singolare esiste solo per l'uno esatto. */
function formattaOre(ore: number): string {
  const numero = String(ore).replace('.', ',');
  return ore === 1 ? `${numero} ora` : `${numero} ore`;
}

function Stato({ stato }: { stato: Evento['stato'] }) {
  const testo =
    stato === 'APPROVATO'
      ? 'Approvata'
      : stato === 'IN_ATTESA'
        ? 'In attesa'
        : stato === 'RIFIUTATO'
          ? 'Rifiutata'
          : 'Registrata';

  const classe =
    stato === 'APPROVATO'
      ? stili.statoApprovato
      : stato === 'IN_ATTESA'
        ? stili.statoInAttesa
        : stato === 'RIFIUTATO'
          ? stili.statoRifiutato
          : stili.statoNeutro;

  return <span className={`${stili.stato} ${classe}`}>{testo}</span>;
}
